import React, { useEffect, useMemo, useState } from 'react';

import { IoClose } from "react-icons/io5";
import { IoMdEye, IoMdEyeOff } from "react-icons/io";

import { apiBaseUrl } from '../../core/globalVariables'

const FilterFeatureDataSimpleTimeseriesModal = ({ globalRefs, globalState }) => {
    const mapMetaData = globalState.mapMetaData;

    const setShowFilterFeatureDataSimpleTimeseriesModal =
        globalState.setShowFilterFeatureDataSimpleTimeseriesModal;

    const setMapMetaData = globalState.setMapMetaData;

    const currentMapIdRef = globalRefs.currentMapIdRef;

    const availableNumericMetrics =
        globalState.availableNumericMetrics || [];

    const availableStringMetrics =
        globalState.availableStringMetrics || [];

    const [numericMetrics, setNumericMetrics] = useState([]);
    const [stringMetrics, setStringMetrics] = useState([]);

    const [draggedMetric, setDraggedMetric] = useState(null);
    const [saving, setSaving] = useState(false);

    const selectedMetric = globalState.selectedMetric;
    const setSelectedMetric = globalState.setSelectedMetric;

    const selectedKey = globalState.selectedKey;
    const setSelectedKey = globalState.setSelectedKey;

    /*
     * Build the initial lists from map metadata.
     *
     * display_order controls the order.
     * display_by_default controls visibility.
     */

    useEffect(() => {
        const metricsMeta =
            mapMetaData?.define_feature_data_simple_timeseries || {};

        const buildMetrics = (availableMetrics) => {
            return availableMetrics
                .map((key) => ({
                    key,
                    meta: metricsMeta[key]
                }))
                .filter((item) => item.meta)
                .sort((a, b) => {
                    const aVisible =
                        a.meta.display_by_default !== false;

                    const bVisible =
                        b.meta.display_by_default !== false;

                    // Visible properties always come before hidden properties
                    if (aVisible !== bVisible) {
                        return aVisible ? -1 : 1;
                    }

                    const orderA =
                        typeof a.meta.display_order === 'number'
                            ? a.meta.display_order
                            : Number.MAX_SAFE_INTEGER;

                    const orderB =
                        typeof b.meta.display_order === 'number'
                            ? b.meta.display_order
                            : Number.MAX_SAFE_INTEGER;

                    if (orderA !== orderB) {
                        return orderA - orderB;
                    }

                    return (a.meta?.name || a.key).localeCompare(
                        b.meta?.name || b.key,
                        undefined,
                        { sensitivity: 'base' }
                    );
                });
        };

        const numeric = buildMetrics(availableNumericMetrics);
        const string = buildMetrics(availableStringMetrics);

        // Set the newly ordered lists
        setNumericMetrics(numeric);
        setStringMetrics(string);

    }, [
        mapMetaData,
        availableNumericMetrics,
        availableStringMetrics
    ]);

    /*
     * Toggle visibility locally.
     *
     * This no longer changes display_order.
     */

    const toggleMetric = (key, group) => {
        const setter =
            group === 'numeric'
                ? setNumericMetrics
                : setStringMetrics;

        setter(prev => {
            const index = prev.findIndex(
                item => item.key === key
            );

            if (index === -1) {
                return prev;
            }

            const item = prev[index];

            const currentlyVisible =
                item.meta.display_by_default !== false;

            const updatedItem = {
                ...item,
                meta: {
                    ...item.meta,
                    display_by_default: !currentlyVisible
                }
            };

            /*
             * Remove the item from its current position.
             */
            const remaining = prev.filter(
                metric => metric.key !== key
            );

            /*
             * Turning ON:
             *
             * Put the property at the bottom of the ON section.
             */
            if (!currentlyVisible) {
                const visible = remaining.filter(
                    metric =>
                        metric.meta.display_by_default !== false
                );

                const hidden = remaining.filter(
                    metric =>
                        metric.meta.display_by_default === false
                );

                return [
                    ...visible,
                    updatedItem,
                    ...hidden
                ];
            }

            /*
             * Turning OFF:
             *
             * Put the property at the bottom of the OFF section.
             */
            const visible = remaining.filter(
                metric =>
                    metric.meta.display_by_default !== false
            );

            const hidden = remaining.filter(
                metric =>
                    metric.meta.display_by_default === false
            );

            return [
                ...visible,
                ...hidden,
                updatedItem
            ];
        });
    };

    /*
     * Drag handling.
     */
    const handleDragStart = (event, key, group) => {
        setDraggedMetric({
            key,
            group
        });

        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData(
            'text/plain',
            JSON.stringify({ key, group })
        );
    };

    const handleDragOver = (event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
    };

    const handleDrop = (event, targetKey, targetGroup) => {
        event.preventDefault();

        if (!draggedMetric) {
            return;
        }

        /*
         * Do not allow dragging between Values and Categories.
         */
        if (draggedMetric.group !== targetGroup) {
            setDraggedMetric(null);
            return;
        }

        const setter =
            targetGroup === 'numeric'
                ? setNumericMetrics
                : setStringMetrics;

        setter(prev => {
            const fromIndex = prev.findIndex(
                item => item.key === draggedMetric.key
            );

            const toIndex = prev.findIndex(
                item => item.key === targetKey
            );

            if (
                fromIndex === -1 ||
                toIndex === -1 ||
                fromIndex === toIndex
            ) {
                return prev;
            }

            const next = [...prev];

            const [movedItem] = next.splice(fromIndex, 1);

            next.splice(toIndex, 0, movedItem);

            return next;
        });

        setDraggedMetric(null);
    };

    const handleDragEnd = () => {
        setDraggedMetric(null);
    };

    /*
     * Confirm all changes.
     *
     * display_order is regenerated from zero independently
     * for Values and Categories.
     *
     * display_by_default is sent alongside it.
     */
    const confirmChanges = async () => {
        try {
            setSaving(true);

            const token = localStorage.getItem('token');

            if (!token) {
                throw new Error('No token in local storage');
            }

            const mapId = currentMapIdRef.current;

            if (!mapId) {
                throw new Error('No current map ID');
            }

            const simpleTimeseries = {};

            numericMetrics.forEach((item, index) => {
                simpleTimeseries[item.key] = {
                    display_order: index,
                    display_by_default:
                        item.meta.display_by_default !== false
                };
            });

            stringMetrics.forEach((item, index) => {
                simpleTimeseries[item.key] = {
                    display_order: index,
                    display_by_default:
                        item.meta.display_by_default !== false
                };
            });

            const payload = {
                feature_header: {
                    define_feature_data_simple_timeseries:
                        simpleTimeseries
                }
            };

            console.log('PATCH PAYLOAD:', payload);

            const url =
                `${apiBaseUrl}/api/maps/${mapId}?srid=4326`;

            const response = await fetch(url, {
                method: 'PATCH',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const errorText = await response.text();

                throw new Error(
                    `PATCH failed: ${response.status} ${errorText}`
                );
            }

            await response.json();

            /*
             * Update global metadata to match the confirmed state.
             */
            setMapMetaData(prev => {
                const next = structuredClone(prev);

                if (
                    !next.define_feature_data_simple_timeseries
                ) {
                    next.define_feature_data_simple_timeseries = {};
                }

                Object.entries(simpleTimeseries).forEach(
                    ([key, values]) => {
                        next.define_feature_data_simple_timeseries[key] = {
                            ...next
                                .define_feature_data_simple_timeseries[key],
                            ...values
                        };
                    }
                );

                return next;
            });

            setShowFilterFeatureDataSimpleTimeseriesModal(false);

            const firstVisibleNumeric =
                numericMetrics.find(
                    item => item.meta.display_by_default !== false
                );

            const firstVisibleString =
                stringMetrics.find(
                    item => item.meta.display_by_default !== false
                );

            if (firstVisibleNumeric) {
                setSelectedMetric(firstVisibleNumeric.key);
            }

            if (firstVisibleString) {
                setSelectedKey(firstVisibleString.key);
            }

        } catch (err) {
            console.error('PATCH failed:', err);
        } finally {
            setSaving(false);
        }
    };

    const renderMetric = (item, group) => {
        const {
            key,
            meta
        } = item;

        const isDefaultVisible =
            meta.display_by_default !== false;

        const isDragging =
            draggedMetric?.key === key &&
            draggedMetric?.group === group;

        return (
            <div
                key={key}
                className={`propertyFilterOption ${isDefaultVisible ? 'is-selected' : 'is-deselected'}`}
                draggable
                onDragStart={event =>
                    handleDragStart(event, key, group)
                }
                onDragOver={handleDragOver}
                onDrop={event =>
                    handleDrop(event, key, group)
                }
                onDragEnd={handleDragEnd}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '6px',
                    marginBottom: '6px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    background: isDefaultVisible
                        ? '#fff'
                        : '#f3f3f3',
                    opacity: isDragging ? 0.5 : 1,
                    cursor: 'grab',
                    userSelect: 'none'
                }}
            >
                <span
                    style={{
                        cursor: 'grab',
                        color: '#999',
                        fontSize: '16px',
                        lineHeight: 1
                    }}
                    title="Drag to reorder"
                >
                    ⋮⋮
                </span>

                <span
                    style={{
                        flex: 1,
                        color: isDefaultVisible
                            ? '#222'
                            : '#999'
                    }}
                >
                    {meta?.name || key}
                </span>

                <button
                    onClick={event => {
                        event.stopPropagation();

                        toggleMetric(key, group);
                    }}
                    style={{
                        border: 'none',
                        background: 'none',
                        padding: '2px',
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        fontSize: '20px',
                        color: isDefaultVisible
                            ? '#222'
                            : '#999'
                    }}
                    title={
                        isDefaultVisible
                            ? 'Hide by default'
                            : 'Show by default'
                    }
                >
                    {isDefaultVisible
                        ? <IoMdEye />
                        : <IoMdEyeOff />
                    }
                </button>
            </div>
        );
    };

    return (
        <div
            style={{
                width: '320px',
                height: '500px',
                backgroundColor: '#fff',
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 10000000,
                borderRadius: '6px',
                padding: '12px',
                boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
            }}
        >
            <div
                style={{
                    width: '320px',
                    maxHeight: '450px',
                    overflowY: 'auto',
                    backgroundColor: '#fff',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}
                >
                    <h4 style={{ margin: 0 }}>
                        Properties Filter
                    </h4>

                    <button
                        onClick={() =>
                            setShowFilterFeatureDataSimpleTimeseriesModal(false)
                        }
                        style={{
                            border: 'none',
                            background: 'none',
                            cursor: 'pointer',
                            paddingRight: '10px'

                        }}
                    >
                        <IoClose />
                    </button>
                </div>

                <h4 style={{ marginTop: 10 }}>
                    Values
                </h4>

                {numericMetrics.map(item =>
                    renderMetric(item, 'numeric')
                )}

                <h4 style={{ marginTop: 20 }}>
                    Categories
                </h4>

                {stringMetrics.map(item =>
                    renderMetric(item, 'string')
                )}

            </div>

            <div style={{
                // position: 'fixed',
                zIndex: '100',
                height: '50px',
                width: '100%'
            }}>
                <button
                    onClick={confirmChanges}
                    disabled={saving}
                    style={{
                        width: '100%',
                        marginTop: '16px',
                        padding: '8px 12px',
                        border: 'none',
                        borderRadius: '6px',
                        background: saving ? '#aaa' : '#073028',
                        color: '#fff',
                        cursor: saving
                            ? 'default'
                            : 'pointer',
                        fontSize: '14px'
                    }}
                >
                    {saving ? 'Saving...' : 'Confirm'}
                </button>
            </div>

        </div>
    );
};

export default FilterFeatureDataSimpleTimeseriesModal;
